/**
 * PersonaEngineConfig — Full configuration page for the Persona Engine.
 *
 * Sections:
 *   1. Engine Selection (EngineSelector component)
 *   2. Modifier Stack (ModifierPanel component)
 *   3. Confidence Thresholds (ThresholdSliders component)
 *   4. State Layer Reversion (number inputs)
 *   5. Injection Profile (radio group)
 *   6. Transparency Mode (radio group)
 *   7. Save / Reset buttons
 *
 * Route: /admin/persona-engine/config
 */

import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { PageHeader, fadeUp } from '@personic/shared'
import { buildBreadcrumbs } from '@/config/routes'
import { usePersonaEngineConfig, useUpdatePersonaEngineConfig, usePersonaEngineEngines } from '@/hooks/usePersonaEngine'
import EngineSelector from '@/components/persona-engine/EngineSelector'
import ModifierPanel from '@/components/persona-engine/ModifierPanel'
import ThresholdSliders from '@/components/persona-engine/ThresholdSliders'
import type {
  ModifierType,
  EngineType,
  InjectionProfile,
  TransparencyMode,
} from '@personic/shared/types/persona-engine'

// ─── Skeleton ────────────────────────────────────────────────────────────────

function ConfigSkeleton() {
  return (
    <div className="max-w-4xl space-y-6">
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          className="rounded-xl border border-warm-200 bg-white p-6 animate-pulse"
        >
          <div className="h-4 w-48 bg-warm-100 rounded mb-4" />
          <div className="space-y-3">
            <div className="h-3 w-full bg-warm-100 rounded" />
            <div className="h-3 w-3/4 bg-warm-100 rounded" />
            <div className="h-10 w-full bg-warm-100 rounded" />
          </div>
        </div>
      ))}
    </div>
  )
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function PersonaEngineConfigPage() {
  const breadcrumbs = buildBreadcrumbs('/admin/persona-engine/config')
  const { data: config, isLoading: configLoading } = usePersonaEngineConfig()
  const { data: engines } = usePersonaEngineEngines()
  const updateConfig = useUpdatePersonaEngineConfig()

  // Local state mirroring config (same pattern as PersonaEngineTab)
  const [engineSelection, setEngineSelection] = useState<string>(config?.engine_selection ?? 'auto')
  const [activeModifiers, setActiveModifiers] = useState<ModifierType[]>(
    (config?.modifier_config?.modifiers as ModifierType[]) ?? []
  )
  const [thresholds, setThresholds] = useState<Record<string, number>>(
    config?.confidence_thresholds ?? {}
  )
  const [reversionMessages, setReversionMessages] = useState(
    config?.state_layer_reversion_messages ?? 5
  )
  const [reversionMinutes, setReversionMinutes] = useState(
    config?.state_layer_reversion_minutes ?? 10
  )
  const [injectionProfile, setInjectionProfile] = useState<InjectionProfile>(
    (config?.injection_profile as InjectionProfile) ?? 'core'
  )
  const [transparencyMode, setTransparencyMode] = useState<TransparencyMode>(
    (config?.transparency_mode as TransparencyMode) ?? 'hidden'
  )
  const [hasChanges, setHasChanges] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)

  // Sync from config when loaded
  useEffect(() => {
    if (config) {
      setEngineSelection(config.engine_selection ?? 'auto')
      setActiveModifiers((config.modifier_config?.modifiers as ModifierType[]) ?? [])
      setThresholds(config.confidence_thresholds ?? {})
      setReversionMessages(config.state_layer_reversion_messages ?? 5)
      setReversionMinutes(config.state_layer_reversion_minutes ?? 10)
      setInjectionProfile((config.injection_profile as InjectionProfile) ?? 'core')
      setTransparencyMode((config.transparency_mode as TransparencyMode) ?? 'hidden')
    }
  }, [config])

  // Track changes
  useEffect(() => {
    if (config) {
      const changed =
        engineSelection !== (config.engine_selection ?? 'auto') ||
        JSON.stringify(activeModifiers) !== JSON.stringify((config.modifier_config?.modifiers ?? [])) ||
        JSON.stringify(thresholds) !== JSON.stringify(config.confidence_thresholds ?? {}) ||
        reversionMessages !== (config.state_layer_reversion_messages ?? 5) ||
        reversionMinutes !== (config.state_layer_reversion_minutes ?? 10) ||
        injectionProfile !== (config.injection_profile ?? 'core') ||
        transparencyMode !== (config.transparency_mode ?? 'hidden')
      setHasChanges(changed)
    }
  }, [config, engineSelection, activeModifiers, thresholds, reversionMessages, reversionMinutes, injectionProfile, transparencyMode])

  const handleSave = async () => {
    try {
      await updateConfig.mutateAsync({
        engine_selection: engineSelection,
        modifier_config: { modifiers: activeModifiers },
        confidence_thresholds: thresholds,
        state_layer_reversion_messages: reversionMessages,
        state_layer_reversion_minutes: reversionMinutes,
        injection_profile: injectionProfile,
        transparency_mode: transparencyMode,
      })
      setSaveSuccess(true)
      setHasChanges(false)
      setTimeout(() => setSaveSuccess(false), 3000)
    } catch (error) {
      console.error('Failed to save Persona Engine config:', error)
    }
  }

  const handleReset = () => {
    if (config) {
      setEngineSelection(config.engine_selection ?? 'auto')
      setActiveModifiers((config.modifier_config?.modifiers as ModifierType[]) ?? [])
      setThresholds(config.confidence_thresholds ?? {})
      setReversionMessages(config.state_layer_reversion_messages ?? 5)
      setReversionMinutes(config.state_layer_reversion_minutes ?? 10)
      setInjectionProfile((config.injection_profile as InjectionProfile) ?? 'core')
      setTransparencyMode((config.transparency_mode as TransparencyMode) ?? 'hidden')
      setHasChanges(false)
    }
  }

  if (configLoading) {
    return (
      <div>
        <PageHeader
          title="Engine Config"
          description="Configure the Persona Engine."
          breadcrumbs={breadcrumbs}
        />
        <ConfigSkeleton />
      </div>
    )
  }

  return (
    <div>
      <PageHeader
        title="Engine Config"
        description="Configure the Persona Engine — engine selection, modifiers, thresholds, and transparency settings."
        breadcrumbs={breadcrumbs}
      />

      <div className="max-w-4xl space-y-6">
        {/* Save success banner */}
        {saveSuccess && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 p-4 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-700"
          >
            <span>Configuration saved successfully!</span>
          </motion.div>
        )}

        {/* Engine Selection */}
        <motion.div variants={fadeUp} initial="hidden" animate="visible">
          <div className="card p-6">
            <EngineSelector
              value={engineSelection}
              onChange={setEngineSelection}
              engines={engines}
              productLens="engage"
            />
          </div>
        </motion.div>

        {/* Modifier Configuration */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.05 }}
        >
          <div className="card p-6">
            <ModifierPanel
              activeModifiers={activeModifiers}
              onChange={setActiveModifiers}
              primaryEngine={engineSelection as EngineType}
            />
          </div>
        </motion.div>

        {/* Confidence Thresholds */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.1 }}
        >
          <div className="card p-6">
            <ThresholdSliders
              thresholds={thresholds}
              onChange={setThresholds}
            />
          </div>
        </motion.div>

        {/* State Layer Reversion */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.15 }}
        >
          <div className="card p-6">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">
              State Layer Reversion
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              How quickly persona state-layer adjustments revert to baseline after a conversation.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reversion after N messages
                </label>
                <input
                  type="number"
                  min={1}
                  max={50}
                  value={reversionMessages}
                  onChange={(e) => setReversionMessages(Math.max(1, parseInt(e.target.value) || 5))}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-personic-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reversion after N minutes
                </label>
                <input
                  type="number"
                  min={1}
                  max={120}
                  value={reversionMinutes}
                  onChange={(e) => setReversionMinutes(Math.max(1, parseInt(e.target.value) || 10))}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-personic-500"
                />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Injection Profile */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.2 }}
        >
          <div className="card p-6">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">
              Injection Profile
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Controls how much persona data is injected into LLM prompts.
            </p>
            <div className="space-y-3">
              {([
                { value: 'core' as InjectionProfile, label: 'Core', description: 'Top 6 dimensions + triggers + floor — minimal token usage' },
                { value: 'full' as InjectionProfile, label: 'Full', description: 'All dimensions, raw computation, and extended metadata' },
              ]).map((option) => (
                <label
                  key={option.value}
                  className={`flex items-start gap-4 p-4 rounded-lg border cursor-pointer transition-colors ${
                    injectionProfile === option.value
                      ? 'border-personic-500 bg-personic-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="injection-profile"
                    value={option.value}
                    checked={injectionProfile === option.value}
                    onChange={() => setInjectionProfile(option.value)}
                    className="mt-1 h-4 w-4 text-personic-600 focus:ring-personic-500 border-gray-300"
                  />
                  <div>
                    <span className="font-medium text-gray-900">{option.label}</span>
                    <p className="text-sm text-gray-500 mt-0.5">{option.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Transparency Mode */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.25 }}
        >
          <div className="card p-6">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-4">
              Transparency Mode
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Controls how persona engine activity is disclosed to end users.
            </p>
            <div className="space-y-3">
              {([
                { value: 'hidden' as TransparencyMode, label: 'Hidden', description: 'Persona engine operates silently — no disclosure to end users' },
                { value: 'configurable' as TransparencyMode, label: 'Configurable', description: 'Tenant admins can choose disclosure level per deployment' },
                { value: 'compliance' as TransparencyMode, label: 'Compliance', description: 'Full transparency — always disclose persona engine usage to end users' },
              ]).map((option) => (
                <label
                  key={option.value}
                  className={`flex items-start gap-4 p-4 rounded-lg border cursor-pointer transition-colors ${
                    transparencyMode === option.value
                      ? 'border-personic-500 bg-personic-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="transparency-mode"
                    value={option.value}
                    checked={transparencyMode === option.value}
                    onChange={() => setTransparencyMode(option.value)}
                    className="mt-1 h-4 w-4 text-personic-600 focus:ring-personic-500 border-gray-300"
                  />
                  <div>
                    <span className="font-medium text-gray-900">{option.label}</span>
                    <p className="text-sm text-gray-500 mt-0.5">{option.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Save / Reset */}
        <div className="flex items-center justify-end gap-4 pt-2 pb-8">
          <button
            onClick={handleReset}
            disabled={!hasChanges}
            className="px-4 py-2 text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Reset
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges || updateConfig.isPending}
            className="px-6 py-2 bg-personic-600 text-white rounded-lg hover:bg-personic-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {updateConfig.isPending ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </div>
    </div>
  )
}
